/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react'
import { capitalize } from 'lodash'

import Toast from 'components/atoms/Toast'
import { useReviewInsuranceVerificationMutation } from 'store/services/labOrder'
import { Order } from 'store/types'

type InsuranceVerification = NonNullable<Order['insuranceVerificationData']>[number]

export const InsuranceReviewForm = ({
    orderId,
    verification,
}: {
    orderId: number
    verification: InsuranceVerification
}) => {
    const [reviewInsuranceVerification, { isLoading }] = useReviewInsuranceVerificationMutation()
    const [selectedReviewStatus, setSelectedReviewStatus] = useState<'APPROVED' | 'FAILED' | ''>('')
    const [failureReason, setFailureReason] = useState('')

    useEffect(() => {
        setSelectedReviewStatus(verification.review?.status || '')
        setFailureReason(verification.review?.reason || '')
    }, [verification.review?.reason, verification.review?.status])

    const handleSubmitReview = async () => {
        if (!selectedReviewStatus) {
            Toast({ type: 'error', label: 'Select approve or fail before submitting.' })
            return
        }

        if (selectedReviewStatus === 'FAILED' && !failureReason.trim()) {
            Toast({ type: 'error', label: 'Provide a reason when marking the review as failed.' })
            return
        }

        try {
            await reviewInsuranceVerification({
                orderId,
                insuranceVerificationId: verification.uuid,
                status: selectedReviewStatus,
                reason: selectedReviewStatus === 'FAILED' ? failureReason.trim() : undefined,
            }).unwrap()

            Toast({
                type: 'success',
                label: `Insurance review saved for ${capitalize(verification.type)} payer.`,
            })
        } catch (error: any) {
            Toast({
                type: 'error',
                label: error?.data?.message || 'Unable to save insurance review.',
            })
        }
    }

    return (
        <div
            css={{
                marginTop: 16,
                padding: 16,
                background: '#ffffff',
                borderRadius: 6,
                border: '1px solid #dddddd',
            }}>
            <div
                css={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#495057',
                    marginBottom: 12,
                }}>
                Staff Review
            </div>
            <div
                css={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: 12,
                }}>
                <button
                    type="button"
                    onClick={() => setSelectedReviewStatus('APPROVED')}
                    css={{
                        padding: '8px 14px',
                        borderRadius: 4,
                        border: selectedReviewStatus === 'APPROVED' ? '1px solid #218838' : '1px solid #cfd4da',
                        background: selectedReviewStatus === 'APPROVED' ? '#e6f4ea' : '#f8f9fa',
                        color: '#343a40',
                        fontSize: 12,
                        fontWeight: 700,
                    }}>
                    APPROVE
                </button>
                <button
                    type="button"
                    onClick={() => setSelectedReviewStatus('FAILED')}
                    css={{
                        padding: '8px 14px',
                        borderRadius: 4,
                        border: selectedReviewStatus === 'FAILED' ? '1px solid #c82333' : '1px solid #cfd4da',
                        background: selectedReviewStatus === 'FAILED' ? '#fdeaea' : '#f8f9fa',
                        color: '#343a40',
                        fontSize: 12,
                        fontWeight: 700,
                    }}>
                    FAIL
                </button>
                {verification.review?.status && (
                    <div
                        css={{
                            fontSize: 12,
                            color: '#6c757d',
                        }}>
                        Current: {verification.review.status}
                    </div>
                )}
            </div>

            {selectedReviewStatus === 'FAILED' && (
                <textarea
                    value={failureReason}
                    onChange={(event) => setFailureReason(event.target.value)}
                    placeholder="Describe what was wrong or missing so the Optum mapping can be improved."
                    css={{
                        width: '100%',
                        minHeight: 96,
                        resize: 'vertical',
                        border: '1px solid #ced4da',
                        borderRadius: 4,
                        padding: 10,
                        fontSize: 13,
                        marginBottom: 12,
                    }}
                />
            )}

            {verification.review?.reviewedAt && (
                <div
                    css={{
                        fontSize: 12,
                        color: '#6c757d',
                        marginBottom: 12,
                    }}>
                    Last reviewed: {new Date(verification.review.reviewedAt).toLocaleString()}
                </div>
            )}

            <button
                type="button"
                disabled={isLoading}
                onClick={handleSubmitReview}
                css={{
                    padding: '8px 14px',
                    borderRadius: 4,
                    border: '1px solid #2c6ecb',
                    background: '#2c6ecb',
                    color: '#ffffff',
                    fontSize: 12,
                    fontWeight: 700,
                    opacity: isLoading ? 0.7 : 1,
                }}>
                {isLoading ? 'Saving...' : 'Save Review'}
            </button>
        </div>
    )
}
